/**
 * ツールプリセット API（/tools 配下の実験的ツール共用）。
 *
 * ツールは `toolId`（ルート slug。例: "phrase-bloom"）で識別し、設定値は
 * ツール固有のスキーマを持つ JSON ペイロードとして保存する。ペイロードの
 * 検証・マイグレーションは各ツールが `schema_version` を使って行う
 * （例: `src/pages/tools/phrase-bloom/utils/presetPayload.ts`）。
 *
 * `is_shared: true` のプリセットは相互フォロー（お互いにフォローし合って
 * いる）ユーザーの共有一覧に表示される。BE 仕様は
 * `soremo-soundcheck/docs/api/tool-presets.md` を参照。
 */
import { createApi } from "@reduxjs/toolkit/query/react";

import { createAuthBaseQuery } from "../../common/utils";
import { HOSTNAME } from "../../constant/host";

// ===== 型定義 =====

/** プリセット 1 件（自分のもの）。payload はツール固有のため unknown。 */
export interface ToolPreset {
  id: string;
  tool_id: string;
  name: string;
  payload: unknown;
  schema_version: number;
  is_shared: boolean;
  created: string;
  modified: string;
}

/** 共有一覧の所有者サマリー。 */
export interface ToolPresetOwner {
  id: number;
  display_name_jp: string;
  display_name_en: string;
  avatar_url: string | null;
}

/** 共有一覧の要素（owner 付き）。 */
export interface SharedToolPreset extends ToolPreset {
  owner: ToolPresetOwner;
}

export interface MyToolPresetsResponse {
  status: string;
  data: {
    presets: ToolPreset[];
  };
}

export interface SharedToolPresetsResponse {
  status: string;
  data: {
    presets: SharedToolPreset[];
    count: number;
    limit: number;
    offset: number;
    has_next: boolean;
  };
}

export interface ToolPresetMutationResponse {
  status: string;
  data?: {
    preset?: ToolPreset;
    deleted?: boolean;
  };
  message?: string;
}

export interface CreateToolPresetArgs {
  toolId: string;
  name: string;
  payload: unknown;
  schemaVersion: number;
  isShared?: boolean;
}

export interface UpdateToolPresetArgs {
  toolId: string;
  presetId: string;
  /** 渡したフィールドのみ部分更新される。 */
  name?: string;
  payload?: unknown;
  schemaVersion?: number;
  isShared?: boolean;
}

export interface DeleteToolPresetArgs {
  toolId: string;
  presetId: string;
}

export interface SharedToolPresetsArgs {
  toolId: string;
  limit?: number;
  offset?: number;
}

// ===== API スライス =====

export const toolPresetsApi = createApi({
  reducerPath: "toolPresetsApi",
  baseQuery: createAuthBaseQuery(HOSTNAME),
  tagTypes: ["MyToolPresets", "SharedToolPresets"],
  endpoints: (builder) => ({
    // 自分のプリセット一覧（更新日時の降順）
    getMyToolPresets: builder.query<MyToolPresetsResponse, string>({
      query: (toolId) => `/api/me/tools/${toolId}/presets`,
      providesTags: (_result, _error, toolId) => [
        { type: "MyToolPresets", id: toolId },
      ],
    }),

    // 作成
    createToolPreset: builder.mutation<
      ToolPresetMutationResponse,
      CreateToolPresetArgs
    >({
      query: ({ toolId, name, payload, schemaVersion, isShared }) => ({
        url: `/api/me/tools/${toolId}/presets`,
        method: "POST",
        body: {
          name,
          payload,
          schema_version: schemaVersion,
          is_shared: isShared ?? false,
        },
      }),
      invalidatesTags: (_result, _error, { toolId }) => [
        { type: "MyToolPresets", id: toolId },
      ],
    }),

    // 部分更新（上書き保存 / リネーム / 共有トグル）
    updateToolPreset: builder.mutation<
      ToolPresetMutationResponse,
      UpdateToolPresetArgs
    >({
      query: ({ toolId, presetId, name, payload, schemaVersion, isShared }) => {
        const body: Record<string, unknown> = {};
        if (name !== undefined) body.name = name;
        if (payload !== undefined) body.payload = payload;
        if (schemaVersion !== undefined) body.schema_version = schemaVersion;
        if (isShared !== undefined) body.is_shared = isShared;
        return {
          url: `/api/me/tools/${toolId}/presets/${presetId}`,
          method: "PATCH",
          body,
        };
      },
      invalidatesTags: (_result, _error, { toolId }) => [
        { type: "MyToolPresets", id: toolId },
      ],
    }),

    // 削除
    deleteToolPreset: builder.mutation<
      ToolPresetMutationResponse,
      DeleteToolPresetArgs
    >({
      query: ({ toolId, presetId }) => ({
        url: `/api/me/tools/${toolId}/presets/${presetId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { toolId }) => [
        { type: "MyToolPresets", id: toolId },
      ],
    }),

    // 相互フォローユーザーの共有プリセット一覧
    getSharedToolPresets: builder.query<
      SharedToolPresetsResponse,
      SharedToolPresetsArgs
    >({
      query: ({ toolId, limit, offset }) => {
        const params = new URLSearchParams();
        if (limit !== undefined) params.set("limit", String(limit));
        if (offset !== undefined) params.set("offset", String(offset));
        const qs = params.toString();
        return `/api/tools/${toolId}/presets/shared${qs ? `?${qs}` : ""}`;
      },
      providesTags: (_result, _error, { toolId }) => [
        { type: "SharedToolPresets", id: toolId },
      ],
    }),
  }),
});

export const {
  useGetMyToolPresetsQuery,
  useCreateToolPresetMutation,
  useUpdateToolPresetMutation,
  useDeleteToolPresetMutation,
  useGetSharedToolPresetsQuery,
} = toolPresetsApi;
